/**
 * SchemaAdaptiveEditor — top-level scan result editor driven by JSON Schema.
 *
 * Renders the full scan result structure with PDF viewer side-by-side.
 * Schema drives field rendering, masking, overlays, and validation.
 * The component owns the data state and sends the complete modified
 * structure back on save.
 *
 * Props:
 *   data: object — the raw scan result (e.g., { accounts: [...] })
 *   schema: object — JSON Schema with x-extensions for the document type
 *   documentType: string — document type identifier
 *   title: string — editor title
 *   pdfUrl/pdfBucket/pdfKey: PDF source
 *   onInteraction: (event) => void — save, copy-to-chat, etc.
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import PDFSidePanel from '../ui/pdf-side-panel';
import SchemaNode from './SchemaNode';
import ConfirmBeforeActionDialog from '../ui/dialog-confirm-before-action';
import useGetSignedUrl from '../../api/signedurl';
import { computeBalanceSummary } from './overlays/RunningBalanceOverlay';
import { computeShareCountReconciliation, computePortfolioReconciliation, computeTransactionArithmetic, computeMarketValueConsistency } from './overlays/InvestmentReconciliationOverlay';
import styles from './SchemaAdaptiveEditor.module.css';

function SchemaAdaptiveEditor({ data: initialData, schema, documentType, artifactType, agentEdited, title, pdfUrl, pdfBucket, pdfKey, documentName = null, onInteraction }) {
  const [data, setData] = useState(initialData || {});
  const [highlightedTxn, setHighlightedTxn] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [showAgentEditedBanner, setShowAgentEditedBanner] = useState(false);
  const [showAgentEditDirtyDialog, setShowAgentEditDirtyDialog] = useState(false);
  const pendingAgentDataRef = useRef(null);
  const undoStackRef = useRef([]);

  // Step 2 (statement summary) is a READ-ONLY view. Its edits used to write a
  // separate artifact that never published (silent data loss) — all corrections
  // now happen in the Step 3 scan editor. Block edits + hide the save controls.
  const readOnly = artifactType === 'statement_summary';

  // Reset state when a fresh render payload arrives (new artifact type or new data).
  // Without this, finalized=true leaks from one view to the next, and stale data
  // from a prior view keeps showing.
  //
  // Special case: if this is an agent-initiated refresh (agentEdited=true) AND the
  // reviewer has unsaved changes, prompt before overwriting. Otherwise accept the
  // new data and surface a banner with an Undo action.
  useEffect(() => {
    if (agentEdited && dirty) {
      pendingAgentDataRef.current = initialData || {};
      setShowAgentEditDirtyDialog(true);
      return;
    }
    setData(initialData || {});
    setDirty(false);
    setFinalized(false);
    setSaving(false);
    setShowFinalizeDialog(false);
    undoStackRef.current = [];
    setShowAgentEditedBanner(!!agentEdited);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, artifactType, agentEdited]);

  const handleAcceptAgentEdit = useCallback(() => {
    const next = pendingAgentDataRef.current || {};
    pendingAgentDataRef.current = null;
    setData(next);
    setDirty(false);
    setFinalized(false);
    setSaving(false);
    setShowFinalizeDialog(false);
    undoStackRef.current = [];
    setShowAgentEditDirtyDialog(false);
    setShowAgentEditedBanner(true);
  }, []);

  const handleKeepMyEdits = useCallback(() => {
    pendingAgentDataRef.current = null;
    setShowAgentEditDirtyDialog(false);
  }, []);

  const handleAgentUndo = useCallback(() => {
    setShowAgentEditedBanner(false);
    if (onInteraction) {
      onInteraction({ type: 'agent_undo_requested' });
    }
  }, [onInteraction]);

  // Pre-signed URL for PDF
  const { data: signedUrl } = useGetSignedUrl(pdfBucket, pdfKey, !!(pdfBucket && pdfKey && !pdfUrl));
  const effectivePdfUrl = pdfUrl || signedUrl;

  // Deep clone
  const clone = useCallback((obj) => JSON.parse(JSON.stringify(obj)), []);

  // Push undo
  const pushUndo = useCallback(() => {
    undoStackRef.current = [...undoStackRef.current, clone(data)];
  }, [data, clone]);

  // Undo
  const handleUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const prev = undoStackRef.current.pop();
    setData(prev);
    setDirty(undoStackRef.current.length > 0);
  }, []);

  // Data change handler — blocked after finalize, and for the read-only
  // statement-summary view (edits there never publish; use the scan editor).
  const handleDataChange = useCallback((newData) => {
    if (finalized || readOnly) return;
    pushUndo();
    setData(newData);
    setDirty(true);
  }, [pushUndo, finalized, readOnly]);

  // Check if all balances reconcile by walking the data for running_balance overlays
  // and investment_reconciliation overlays.
  const allReconciled = useMemo(() => {
    if (!schema || !data) return false;
    const accounts = data.accounts || [];
    if (accounts.length === 0) return false;
    const docType = schema?.['x-document-type'];

    // Investment and retirement statements are hybrid — they use the holdings
    // reconciliation model, not the bank/CC running-balance path.
    if (['investment_statement', 'retirement_statement'].includes(docType)) {
      let allOk = true;
      for (const acct of accounts) {
        for (const stmt of (acct.statements || [])) {
          const txns = stmt.transactions || [];
          // securities and cash_positions are now nested at the statement level
          const securities = stmt.securities || [];
          const cashPositions = stmt.cash_positions || [];
          // Run all 4 investment reconciliation checks
          const shareRecon = computeShareCountReconciliation(txns, securities);
          const portfolioRecon = computePortfolioReconciliation(stmt, securities, cashPositions);
          const txnArithmetic = computeTransactionArithmetic(txns);
          const mvConsistency = computeMarketValueConsistency(securities);
          const checks = [shareRecon.passed, portfolioRecon.passed, txnArithmetic.passed, mvConsistency.passed];
          if (checks.some(c => c === false)) {
            allOk = false;
            break;
          }
        }
        if (!allOk) break;
      }
      return allOk;
    }

    // Standard bank/CC: running balance reconciliation
    for (const acct of accounts) {
      for (const stmt of (acct.statements || [])) {
        const txns = stmt.transactions;
        if (!Array.isArray(txns)) continue;
        const summary = computeBalanceSummary(stmt, txns);
        if (summary.hasDiscrepancy) return false;
      }
    }
    return true;
  }, [schema, data]);

  // Structural gate for finalizing: no two accounts may share the same identity
  // (account_number_last4 + normalized sub_account_number). A duplicate means a
  // sub-account was collapsed — e.g. a dropped "S"/"L" prefix on a credit-union
  // combined statement, which publishes a duplicate account AND hides a missing
  // one. Mirrors the backend within-scan uniqueness gate; the fuller
  // account/statement match against the summary is enforced server-side on
  // approve. Alias-path accounts (no printed number) are skipped.
  const noDuplicateAccounts = useMemo(() => {
    const accounts = data?.accounts || [];
    const seen = new Set();
    for (const a of accounts) {
      const last4 = (a?.account_number_last4 || '').trim();
      if (!last4) continue;
      const sub = (a?.sub_account_number || '').replace(/\s+/g, '').toUpperCase();
      const key = `${last4}|${sub}`;
      if (seen.has(key)) return false;
      seen.add(key);
    }
    return true;
  }, [data]);

  // Every transaction must record which source page it came from (forensic
  // traceability; a page-less line also fails backend scoring and crashes the
  // publish path). Required for bank/CC scans; investment/retirement holdings are
  // exempt (snapshot model), matching the backend SNAPSHOT_RELAXED_CHECKS scoping.
  // Blocks BOTH Save Draft and Finalize — a page-less line must never be persisted.
  const missingPageCount = useMemo(() => {
    if (artifactType && artifactType !== 'scan_result') return 0;
    const docType = schema?.['x-document-type'];
    if (['investment_statement', 'retirement_statement'].includes(docType)) return 0;
    let count = 0;
    for (const acct of (data?.accounts || [])) {
      for (const stmt of (acct.statements || [])) {
        for (const t of (stmt.transactions || [])) {
          const pn = t?.page_number;
          const valid = pn !== null && pn !== undefined && pn !== '' &&
            Number.isFinite(Number(pn)) && Number(pn) >= 1;
          if (!valid) count += 1;
        }
      }
    }
    return count;
  }, [schema, data, artifactType]);
  const allTransactionsHavePage = missingPageCount === 0;

  const missingPageTitle = allTransactionsHavePage
    ? undefined
    : `${missingPageCount} transaction${missingPageCount === 1 ? '' : 's'} ` +
      `missing a page number — every line must record which page it appears on. ` +
      `Add the page number in the Page column before saving.`;

  const canFinalize = allReconciled && noDuplicateAccounts && allTransactionsHavePage;

  // Save draft — saves progress at any time. The ONE hard requirement even for a
  // draft is a page number on every transaction (see missingPageCount): a page-less
  // line is never persisted, so the endpoint would reject it anyway.
  const handleSaveDraft = useCallback(() => {
    if (!allTransactionsHavePage) return;
    if (onInteraction) {
      setSaving(true);
      onInteraction({
        type: 'save_corrections',
        data: clone(data),
        artifactType: artifactType || 'scan_result',
        draft: true,
      });
      setTimeout(() => {
        setDirty(false);
        setSaving(false);
        undoStackRef.current = [];
      }, 500);
    }
  }, [data, clone, onInteraction, allTransactionsHavePage, artifactType]);

  // Find fields with x-warn-if-negative that are currently negative.
  // Returns an array of { label, value, path } describing each hit.
  const negativeBalanceWarnings = useMemo(() => {
    const warnings = [];
    const walk = (node, nodeSchema, path) => {
      if (!node || !nodeSchema) return;
      if (nodeSchema.type === 'object' && nodeSchema.properties) {
        for (const [key, fieldSchema] of Object.entries(nodeSchema.properties)) {
          const val = node[key];
          if (fieldSchema['x-warn-if-negative'] && typeof val === 'number' && val < 0) {
            warnings.push({
              label: fieldSchema['x-display-label'] || key,
              value: val,
              path: [...path, key].join('.'),
            });
          }
          if (val && typeof val === 'object') {
            walk(val, fieldSchema, [...path, key]);
          }
        }
      } else if (Array.isArray(node) && nodeSchema.items) {
        node.forEach((item, idx) => walk(item, nodeSchema.items, [...path, `[${idx}]`]));
      } else if (nodeSchema.items && node && typeof node === 'object') {
        // object containing arrays handled above
      }
    };
    walk(data, schema, []);
    return warnings;
  }, [data, schema]);

  const [showNegativeWarningDialog, setShowNegativeWarningDialog] = useState(false);

  // Finalize — show confirmation dialog (check negatives first)
  const handleFinalizeClick = useCallback(() => {
    if (!canFinalize) return;
    if (negativeBalanceWarnings.length > 0) {
      setShowNegativeWarningDialog(true);
      return;
    }
    setShowFinalizeDialog(true);
  }, [canFinalize, negativeBalanceWarnings]);

  // Confirmed finalize — saves as final
  const handleFinalizeConfirm = useCallback(() => {
    setShowFinalizeDialog(false);
    if (onInteraction) {
      setSaving(true);
      onInteraction({
        type: 'save_corrections',
        data: clone(data),
        artifactType: artifactType || 'scan_result',
        draft: false,
      });
      setTimeout(() => {
        setDirty(false);
        setSaving(false);
        setFinalized(true);
        undoStackRef.current = [];
      }, 500);
    }
  }, [data, clone, onInteraction]);

  // Child interaction handler
  const handleChildInteraction = useCallback((event) => {
    if (event.type === 'transaction_selected') {
      const txn = event.data;
      if (txn) {
        setHighlightedTxn({
          description: txn.description || '',
          amount: txn.amount,
          posting_date: txn.posting_date || txn.date || '',
          page_number: txn.page_number || 1,
          _key: Date.now(),
        });
      }
    }
    // Propagate copy_to_chat etc. to parent (but not save_corrections)
    if (onInteraction && event.type !== 'save_corrections') {
      onInteraction(event);
    }
  }, [onInteraction]);

  const leftPanel = (
    <div className={styles.container}>
      {showAgentEditedBanner && (
        <div className={styles.agentEditedBanner}>
          <span>The assistant just updated this document.</span>
          <button
            type="button"
            className={styles.agentEditedUndoBtn}
            onClick={handleAgentUndo}
          >
            Undo
          </button>
          <button
            type="button"
            className={styles.agentEditedDismissBtn}
            onClick={() => setShowAgentEditedBanner(false)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      {/* Top bar */}
      <div className={styles.topBar}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>{title || 'Scan Result Editor'}</h3>
          {documentType && <span className={styles.docType}>{documentType.replace(/_/g, ' ')}</span>}
        </div>
        <div className={styles.actions}>
          {readOnly ? (
            <span className={styles.finalizedStatus}>Read-only — make corrections in the Scan Editor (Step 3)</span>
          ) : finalized ? (
            <span className={styles.finalizedStatus}>Finalized — submitted for scoring</span>
          ) : (
            <>
              <button className={styles.undoBtn} onClick={handleUndo} disabled={undoStackRef.current.length === 0}>
                ↩ Undo
              </button>
              <button
                className={styles.draftBtn}
                onClick={handleSaveDraft}
                disabled={saving || !dirty || !allTransactionsHavePage}
                title={missingPageTitle}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                className={styles.finalizeBtn}
                onClick={handleFinalizeClick}
                disabled={saving || !canFinalize}
                title={
                  !allTransactionsHavePage
                    ? missingPageTitle
                    : (!noDuplicateAccounts
                      ? 'Two accounts share the same identifier — resolve the duplicate before finalizing.'
                      : (!allReconciled ? 'Balances do not reconcile yet — resolve the discrepancy before finalizing.' : undefined))
                }
              >
                Finalize
              </button>
            </>
          )}
        </div>
      </div>

      {/* Schema-driven content */}
      <div className={styles.scrollArea}>
        {schema ? (
          <SchemaNode
            data={data}
            schema={schema}
            onChange={handleDataChange}
            onInteraction={handleChildInteraction}
            rootData={data}
            defaultExpanded={true}
            readOnly={readOnly}
          />
        ) : (
          <div className={styles.noSchema}>
            <p>No schema available for document type: {documentType}</p>
            <p>Displaying raw data. Fields may not be properly formatted.</p>
            <pre className={styles.rawJson}>{JSON.stringify(data, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <PDFSidePanel
        leftChild={leftPanel}
        fileUrl={effectivePdfUrl}
        documentName={documentName}
        highlightedTxn={highlightedTxn}
      />
      <ConfirmBeforeActionDialog
        open={showFinalizeDialog}
        title="Finalize Corrections"
        confirmMessage="All balances reconcile. Submit these corrections as final? The agent will score and process the result."
        proceedButtonLabel="Submit Final"
        cancelButtonLabel="Cancel"
        onProceed={handleFinalizeConfirm}
        onCancel={() => setShowFinalizeDialog(false)}
        list_element={() => null}
      />
      <ConfirmBeforeActionDialog
        open={showAgentEditDirtyDialog}
        title="Assistant made changes"
        confirmMessage="The assistant has applied changes to this document, but you have unsaved edits. Loading the new version will discard your unsaved edits."
        proceedButtonLabel="Load assistant's version"
        cancelButtonLabel="Keep my edits"
        onProceed={handleAcceptAgentEdit}
        onCancel={handleKeepMyEdits}
        list_element={() => null}
      />
      <ConfirmBeforeActionDialog
        open={showNegativeWarningDialog}
        title="Verify Negative Balances"
        confirmMessage={
          `The following ${negativeBalanceWarnings.length === 1 ? 'balance is' : 'balances are'} negative. ` +
          `This typically indicates an overdraft (debit account) or overpayment (credit account). ` +
          `Please verify these values match the source document before finalizing:\n\n` +
          negativeBalanceWarnings.map(w => `• ${w.label}: ${w.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`).join('\n') +
          `\n\nContinue with finalization?`
        }
        proceedButtonLabel="Values Are Correct — Continue"
        cancelButtonLabel="Go Back and Review"
        onProceed={() => { setShowNegativeWarningDialog(false); setShowFinalizeDialog(true); }}
        onCancel={() => setShowNegativeWarningDialog(false)}
        list_element={() => null}
      />
    </>
  );
}

export default SchemaAdaptiveEditor;
